import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Users, AlertCircle, UserPlus } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { addCandidatesToOffer } from '../../../../../../../utils/api_service/JobOpenings';

interface EmptyStateProps {
  jobOpeningId: string;
  addCandidatesFromPreviousRound: (candidateIds: string[]) => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  jobOpeningId,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const ES = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.emptyState;
  const [isAddingCandidates, setIsAddingCandidates] = useState(false);

  const handleAddCandidates = async () => {
    setIsAddingCandidates(true);
    try {
      await addCandidatesToOffer(jobOpeningId);
      toast.success(t(ES.successToast), {
        position: 'bottom-center',
      });
      window.location.reload();
    } catch {
      toast.error(t(ES.failedToast), {
        position: 'bottom-center',
      });
    } finally {
      setIsAddingCandidates(false);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        borderRadius: 4,
        border: 1,
        borderColor: 'divider',
        p: { xs: 4, md: 6 },
        textAlign: 'center',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 440, mx: 'auto' }}>
        <Box
          sx={{
            mb: 3,
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'primary.50',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Users size={40} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
          {t(ES.title)}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {t(ES.subtitle)}
        </Typography>

        <Button
          variant="contained"
          color="success"
          onClick={handleAddCandidates}
          disabled={isAddingCandidates}
          startIcon={
            isAddingCandidates ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <UserPlus size={18} />
            )
          }
          sx={{ textTransform: 'none', borderRadius: 2, px: 3, py: 1.25, fontWeight: 600 }}
        >
          {isAddingCandidates ? t(ES.adding) : t(ES.addButton)}
        </Button>
      </Box>
    </Paper>
  );
};

export const ErrorFallback: React.FC<{ error: string }> = () => {
  const { t } = useTranslation(NS.adminPages);
  const ERR = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.emptyState.error;
  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        borderRadius: 4,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 3,
          py: { xs: 6, md: 8 },
        }}
      >
        <Box
          sx={{
            mb: 3,
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'error.50',
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AlertCircle size={40} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
          {t(ERR.title)}
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            bgcolor: 'info.50',
            borderColor: 'info.light',
            borderRadius: 2,
            p: 2.5,
            mb: 4,
            maxWidth: 420,
            width: '100%',
            textAlign: 'left',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.dark', mb: 1 }}>
            {t(ERR.suggestedActions)}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { fontSize: '13px', color: 'text.secondary', mb: 0.5 } }}>
            <li>{t(ERR.checkResults)}</li>
            <li>{t(ERR.verifyId)}</li>
            <li>{t(ERR.tryRefreshing)}</li>
          </Box>
        </Paper>
      </Box>
    </Paper>
  );
};

export default EmptyState;
